import {
  Box,
  Button,
  ConfirmCancelDialog,
  Text,
  TextField,
  useDialogState,
  useToast,
} from '@color/continuum';
import { type ReactNode, useState } from 'react';

import { useCancelVariantConfirmationsMutation } from './api';
import { SelectedConfirmationsTable } from './SelectedConfirmationsTable';
import type { VariantConfirmation } from './types';
import { VariantConfirmationState } from './types';
import { usePrimerFailureColumns } from './usePrimerFailureColumns';

export function isVariantConfirmationStateCancellable(state: VariantConfirmationState): boolean {
  return state === VariantConfirmationState.NEW;
}

interface CancellationDialogProps {
  confirmations: VariantConfirmation[];
  onClose: () => void;
  onSuccess: () => void;
  dialogVariant?: 'batch' | 'single';
}

export const CancellationDialog = ({
  confirmations,
  onClose,
  onSuccess,
  dialogVariant = 'batch',
}: CancellationDialogProps) => {
  const [cancelNotes, setCancelNotes] = useState('');

  const { showToast } = useToast();
  const { mutateAsync: cancelConfirmations, isPending } = useCancelVariantConfirmationsMutation();

  const { extensionColumns, isPrimerNotesIncomplete, getFailPrimers } =
    usePrimerFailureColumns(confirmations);

  const plural = confirmations.length !== 1 ? 's' : '';
  const isSingleLayout = dialogVariant === 'single';

  const title = isSingleLayout
    ? 'Cancel confirmation'
    : `Cancel ${confirmations.length} variant confirmation${plural}`;

  const confirmButtonText = isSingleLayout ? 'Cancel Confirmation' : `Cancel Confirmation${plural}`;

  const handleSubmit = async () => {
    try {
      await cancelConfirmations({
        confirmations: confirmations.map((c) => ({
          id: c.id,
          failPrimers: getFailPrimers(c.id),
        })),
        notes: cancelNotes,
      });
      showToast(`Variant confirmation${plural} cancelled.`, { severity: 'success' });
      onSuccess();
    } catch {
      // mutateAsync rethrows even when onError is defined; catch here to prevent
      // an unhandled rejection. Error toasts are shown by the mutation hook.
    }
  };

  const isSubmitDisabled = isPending || cancelNotes.trim() === '' || isPrimerNotesIncomplete;

  return (
    <ConfirmCancelDialog
      title={title}
      confirmButtonText={confirmButtonText}
      confirmButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={handleSubmit}
      onCancel={onClose}
      dialogIsOpen
      fullWidth
      maxWidth="lg"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          marginBottom: 2,
        }}
      >
        {isSingleLayout && (
          <Text>This will cancel the variant confirmation and stop further processing.</Text>
        )}
        <SelectedConfirmationsTable
          confirmations={confirmations}
          extensionVerticalAlign="top"
          showAccessionColumn={!isSingleLayout}
          showGhgvsColumn={!isSingleLayout}
          showSampleColumn={false}
          extensionColumns={extensionColumns}
        />
      </Box>
      <TextField
        label="Reason for cancelling (overwrites internal notes)"
        name="cancelNotes"
        multiline
        minRows={5}
        value={cancelNotes}
        optionalIndicatorIsEnabled={false}
        onChange={(e) => setCancelNotes(e.target.value)}
        fullWidth
        required
      />
    </ConfirmCancelDialog>
  );
};

export function useVariantConfirmationCancelAction(
  confirmation: VariantConfirmation,
  options: { onSuccess: () => void }
): { button: ReactNode | null } {
  const { dialogIsOpen, openDialog, closeDialog } = useDialogState();

  if (!isVariantConfirmationStateCancellable(confirmation.state)) {
    return { button: null };
  }

  const button = (
    <>
      <Button variant="outlined" size="small" color="error" onClick={openDialog}>
        Cancel
      </Button>
      {dialogIsOpen && (
        <CancellationDialog
          confirmations={[confirmation]}
          dialogVariant="single"
          onClose={closeDialog}
          onSuccess={() => {
            closeDialog();
            options.onSuccess();
          }}
        />
      )}
    </>
  );

  return { button };
}
